import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Users, FileText, Shield, Repeat, ClipboardCheck } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto } from "@/lib/mailto";

const vragen = [
  {
    q: "Voor welke doelgroep is het bedoeld?",
    a: "Inwoners die langdurig stilstaan, meerdere belemmeringen ervaren en onvoldoende aansluiting vinden bij bestaande trajecten. Voor hen is een directe stap naar werk of een regulier re-integratietraject nog niet vanzelfsprekend.",
    tag: "Doelgroep",
  },
  {
    q: "Hoe selecteren we deelnemers?",
    a: "De gemeente doet een voordracht. Samen met uw klantmanagers voeren wij een intake uit. Deelname is vrijwillig maar niet vrijblijvend.",
    tag: "Selectie",
  },
  {
    q: "Hoe groot is een groep?",
    a: "Bij voorkeur 20–22 deelnemers, met ruimte voor maximaal 24 waar verantwoord. Twee trainers per groep.",
    tag: "Formaat",
  },
  {
    q: "Hoe lang duurt het traject?",
    a: "LevelLeven START duurt 12 weken en is een afgerond programma op zichzelf. Daarna volgt, afhankelijk van de deelnemer en de gemeentelijke afspraken, een vervolg richting participatie, scholing, begeleiding of werk. Een mogelijk vervolgprogramma (VERDER) is geen automatische voortzetting; duur en intensiteit daarvan zijn nog niet vastgesteld.",
    tag: "Doorlooptijd",
  },
  {
    q: "Wat kost het?",
    a: "Voorlopig uitgangspunt voor de gemeentelijke START-variant: circa €5.000 per deelnemer, afhankelijk van de definitieve uitvoering en afspraken. Rekenvoorbeeld: bij 20 deelnemers is de indicatieve groepskosten €100.000. Voor VERDER noemen we nog geen prijs. We publiceren geen besparingen of terugverdientijden zolang die niet onderbouwd zijn.",
    tag: "Kosten",
  },
  {
    q: "Wat krijgen wij gerapporteerd?",
    a: "Een geanonimiseerde groepsrapportage na de tussen- en eindmeting, en indien geautoriseerd een individuele voortgangsrapportage. Ontwikkelgebieden zijn onder meer zelfregie, dagstructuur, sociale vaardigheden, vertrouwen en toekomstperspectief, aangevuld met aanwezigheid, deelname en de gekozen vervolgstap.",
    tag: "Rapportage",
    link: { to: "/voor-opdrachtgevers#dashboard", label: "Bekijk voorbeeldrapportage" },
  },
  {
    q: "Hoe wordt resultaat gemeten?",
    a: "Met een startmeting, een of meer tussenmetingen, een eindmeting en waar afgesproken een vervolgmeting. De LevelLeven-meetset is nog niet wetenschappelijk gevalideerd. Bij pilots werken we bij voorkeur samen met een onafhankelijke onderzoekspartij voor een effect- en procesevaluatie.",
    tag: "Meten",
  },
  {
    q: "Hoe past het binnen bestaand beleid?",
    a: "LevelLeven is een aanvullende ontwikkelfase, geen vervanging van uw re-integratieaanbod. Samen met u kijken we hoe de aanmeldroute en de overdracht naar bestaande voorzieningen het best aansluiten.",
    tag: "Beleid",
    link: { to: "/kennisbank/vng-model-verordening", label: "Lees over de VNG-verordening" },
  },
  {
    q: "Welke rol heeft de klantmanager?",
    a: "De klantmanager blijft casusregisseur en houder van het dossier. Wij zijn uitvoerder. Een warme overdracht bij de start, contact tijdens de twaalf weken en een warme overdracht naar de volgende stap. Bij zorgen schakelen we direct.",
    tag: "Samenwerking",
  },
  {
    q: "Wat gebeurt er bij uitval?",
    a: "Uitval wordt zo vroeg mogelijk gesignaleerd via de tweewekelijkse bijeenkomsten en de buddy-structuur. Bij dreigende uitval schakelt de trainer de casemanager in. Als uitval niet te voorkomen is, legt LevelLeven vast wat de reden is en geeft advies over een alternatief vervolgpad. Dit wordt opgenomen in de rapportage aan de gemeente. Concrete uitvalcijfers worden per pilot gerapporteerd.",
    tag: "Uitval",
  },
  {
    q: "Hoe organiseren jullie privacy?",
    a: "AVG-conform. Wij zijn verwerker; u bent verwerkingsverantwoordelijke. Standaard verwerkersovereenkomst beschikbaar, DPIA op verzoek. Deelnemers geven expliciete toestemming voor iedere gegevensdeling, inclusief richting werkgevers, EVC-partij en gemeente. Rapportages zijn geanonimiseerd tenzij anders overeengekomen.",
    tag: "Privacy",
  },
];

const dashboardIndicators = [
  { label: "Actieve deelname", start: 35, halfweg: 82, unit: "%" },
  { label: "Dagstructuur", start: 42, halfweg: 71, unit: "%" },
  { label: "Vertrouwen eigen kunnen", start: 3.8, halfweg: 6.4, unit: "/10", scale: 10 },
  { label: "Participatie (16u+ / vrijwillig)", start: 20, halfweg: 55, unit: "%" },
  { label: "Belastbaarheid (WHODAS)", start: 34, halfweg: 62, unit: "%" },
];

export default function VoorOpdrachtgevers() {
  usePageMeta({
    title: "Voor gemeenten en UWV, LevelLeven",
    description:
      "Elf directe antwoorden op de vragen die opdrachtgevers stellen. Plus een tastbare voorbeeldrapportage.",
  });
  return (
    <div>
      {/* HERO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-8">
              <SectionLabel>Voor gemeenten en UWV</SectionLabel>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight text-[#1D2A22] font-light">
                Elf vragen.{" "}
                <span className="italic text-[#E06B3F]">Elf directe antwoorden</span>.
              </h1>
              <p className="mt-8 text-lg text-[#4A5D4E] leading-relaxed font-light max-w-2xl">
                Wat u wilt weten voor u een pilotgesprek plant, inclusief
                een tastbare voorbeeldrapportage van een fictieve groep.
                Geen verkooppraat. Alleen antwoorden.
              </p>
            </div>
            <div className="lg:col-span-4">
              <a
                href="#vragen"
                className="group inline-flex items-center gap-3 border border-[#1D2A22] hover:bg-[#1D2A22] hover:text-[#FAF7F1] rounded-full pl-6 pr-2 py-2 transition-colors text-sm"
              >
                <span>Naar de vragen</span>
                <span className="border border-[#1D2A22] group-hover:border-[#FAF7F1] w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                  <ArrowDown size={14} />
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Q&A LIST */}
      <section id="vragen" className="py-16 sm:py-24 border-t border-[#E5E1D8]">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <ol className="divide-y divide-[#E5E1D8] border-y border-[#E5E1D8]">
            {vragen.map((v, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: (i % 5) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="py-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10"
                data-testid={`opdrachtgever-vraag-${i}`}
              >
                <div className="md:col-span-3">
                  <p className="chapter-num text-6xl md:text-7xl text-[#E06B3F] leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#7A8276]">
                    {v.tag}
                  </p>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-heading text-2xl md:text-3xl text-[#1D2A22] leading-tight font-light">
                    {v.q}
                  </h2>
                  <p className="mt-4 text-[#4A5D4E] leading-relaxed">{v.a}</p>
                  {v.link && (
                    <Link
                      to={v.link.to}
                      className="mt-4 inline-flex items-center gap-2 text-sm text-[#E06B3F] hover:text-[#1D2A22] link-underline"
                    >
                      {v.link.label} <ArrowUpRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* DASHBOARD DEMO */}
      <section id="dashboard" className="py-20 sm:py-32 bg-[#EFECE5]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <SectionLabel>Voorbeeldrapportage</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light">
                Groep Ridderdam,{" "}
                <span className="italic text-[#E06B3F]">na zes maanden</span>.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-[#4A5D4E] leading-relaxed font-light">
                Een fictieve groep, zes maanden onderweg. Zo
                ziet uw kwartaalrapportage eruit, geanonimiseerd en meetbaar.
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-[#FAF7F1] border border-[#E5E1D8] overflow-hidden" data-testid="dashboard-demo">
            <header className="p-6 sm:p-8 border-b border-[#E5E1D8] flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F]">Cohort Q1-Q2 · 2027</p>
                <p className="font-heading text-3xl text-[#1D2A22] mt-1">Groep Ridderdam</p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-[#EFECE5] px-3 py-1 text-[#4A5D4E]">Start-meting 15 jan</span>
                <span className="rounded-full bg-[#EFECE5] px-3 py-1 text-[#4A5D4E]">Tussenmeting 15 jul</span>
                <span className="rounded-full bg-[#1D2A22] text-[#FAF7F1] px-3 py-1">Voortgang: op koers</span>
              </div>
            </header>
            <div className="p-6 sm:p-8 space-y-6">
              {dashboardIndicators.map((ind) => {
                const scale = ind.scale || 100;
                const startPct = (ind.start / scale) * 100;
                const halfPct = (ind.halfweg / scale) * 100;
                const delta = ind.halfweg - ind.start;
                return (
                  <div key={ind.label} className="group" data-testid={`indicator-${ind.label}`}>
                    <div className="flex justify-between items-baseline mb-2">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A8276]">
                        {ind.label}
                      </p>
                      <p className="text-sm text-[#1D2A22]">
                        <span className="text-[#7A8276]">{ind.start}{ind.unit}</span>
                        <span className="mx-2 text-[#E06B3F]">→</span>
                        <span className="font-medium">{ind.halfweg}{ind.unit}</span>
                        <span className="ml-3 text-xs text-[#E06B3F]">+{delta.toFixed(1)}{ind.unit === "/10" ? "" : ind.unit}</span>
                      </p>
                    </div>
                    <div className="relative h-2.5 bg-[#EFECE5] rounded-full overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#E06B3F]/25"
                        style={{ width: `${startPct}%` }}
                      />
                      <motion.div
                        initial={{ width: `${startPct}%` }}
                        whileInView={{ width: `${halfPct}%` }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-y-0 left-0 bg-[#E06B3F]"
                      />
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-[#E5E1D8] pt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { k: "Vervolgstap ingezet", v: "12 / 20", d: "individueel plan" },
                  { k: "EVC gestart", v: "8 / 20", d: "via VIGOR" },
                  { k: "Vrijwilligerswerk", v: "14 / 20", d: "min. 8 uur / week" },
                  { k: "Uitval", v: "1 / 20", d: "warme overdracht" },
                ].map((x) => (
                  <div key={x.k}>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A8276]">{x.k}</p>
                    <p className="font-heading text-3xl text-[#1D2A22] mt-1">{x.v}</p>
                    <p className="text-xs text-[#7A8276] mt-0.5">{x.d}</p>
                  </div>
                ))}
              </div>
            </div>
            <footer className="p-6 sm:p-8 border-t border-[#E5E1D8] bg-[#EFECE5]/60">
              <p className="text-[11px] text-[#7A8276] leading-relaxed italic">
                Voorbeelddata ter illustratie. Aan bovenstaande waarden kunnen
                geen resultaten of garanties worden ontleend. In een reële
                kwartaalrapportage worden zowel groepsniveau als (mits
                geautoriseerd) individuele voortgang gerapporteerd, plus
                kwalitatieve narratieven per deelnemer.
              </p>
            </footer>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="rounded-3xl bg-[#1D2A22] text-[#FAF7F1] px-8 sm:px-16 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <SectionLabel color="#D19B72">Volgende stap</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Een oriënterend gesprek,{" "}
                <span className="italic text-[#D19B72]">binnen twee weken</span>.
              </h2>
              <p className="mt-6 text-[#FAF7F1]/70 max-w-xl">
                We nemen samen door of LevelLeven past bij uw begroting, uw
                doelgroep en uw politieke agenda. Zonder verplichting.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a
                href={buildPilotMailto({
                  subject: "Pilotgesprek LevelLeven",
                  body: "Beste LevelLeven,\n\nWij willen graag een oriënterend gesprek plannen.\n\nOrganisatie:\nContactpersoon:\nGewenste week:\n\nMet vriendelijke groet,",
                })}
                className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1D2A22] rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
              >
                <span className="text-base font-medium">Plan een verkennend gemeentegesprek</span>
                <span className="bg-[#FAF7F1] text-[#1D2A22] w-12 h-12 rounded-full flex items-center justify-center">
                  <ArrowUpRight size={20} />
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
